import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ProductsPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import heroBg from '../assets/tra4.jpeg';
const ProductsPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  
  const FALLBACK_CATEGORIES = useMemo(() => [], []);
  const FALLBACK_PRODUCTS = useMemo(() => [], []);
  
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialSearch = searchParams.get('search') || searchParams.get('q') || '';
  const [search, setSearch] = useState(initialSearch);

  useEffect(() => {
    const q = searchParams.get('search') || searchParams.get('q') || '';
    setSearch(q);
  }, [searchParams]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const suggestions = useMemo(() => {
    const query = search ? search.trim().toLowerCase() : '';
    if (!query) return [];
    
    const matchedCategories = categories.filter(c => {
      const name = c.name ? String(c.name).toLowerCase() : '';
      return name.includes(query);
    }).map(c => ({ ...c, type: 'category' }));

    const matchedProducts = allProducts.filter(p => {
      const name = p.name ? String(p.name).toLowerCase() : '';
      return name.includes(query);
    }).map(p => ({ ...p, type: 'product' }));

    return [...matchedCategories, ...matchedProducts];
  }, [search, allProducts, categories]);

  const envApiUrl = process.env.REACT_APP_API_URL?.trim();
  const enableLocalApi = process.env.REACT_APP_ENABLE_LOCAL_API === 'true';
  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.');
  const API_URL = envApiUrl || '/api';
  const useStaticCatalogOnly = process.env.NODE_ENV === 'development'
    && isLocalHost
    && !!envApiUrl
    && envApiUrl.includes('localhost')
    && !enableLocalApi;
  const fetchConfig = useMemo(() => ({
    apiUrl: API_URL,
    useStaticCatalogOnly
  }), [API_URL, useStaticCatalogOnly]);

  useEffect(() => {
    const fetchData = async () => {
      if (fetchConfig.useStaticCatalogOnly) {
        setError('');
        setAllProducts(FALLBACK_PRODUCTS);
        setCategories(FALLBACK_CATEGORIES);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        // Pull a large page so All Products shows the full existing catalog.
        const prodRes = await fetch(`${fetchConfig.apiUrl}/products?all=true&limit=1000&select=name,description,images,category,otherProductCategory,slug,status,price`, {
          headers: {
          }
        });
        const prodJSON = await prodRes.json();

        const catRes = await fetch(`${fetchConfig.apiUrl}/categories`, {
          headers: {
          }
        });
        const catJSON = await catRes.json();

        if (!prodRes.ok || !prodJSON.success) {
          throw new Error(prodJSON.message || 'Failed to load products');
        }

        const products = Array.isArray(prodJSON.data) ? prodJSON.data : [];

        // Fetch Other Products
        let otherProducts = [];
        try {
          const otherRes = await fetch(`${fetchConfig.apiUrl}/other-products`);
          if (otherRes.ok) {
            const otherJSON = await otherRes.json();
            if (otherJSON.success) {
              otherProducts = Array.isArray(otherJSON.data) ? otherJSON.data : [];
            }
          }
        } catch(e) { console.error('Failed to fetch other products', e); }

        const combined = [...products, ...otherProducts];
        setAllProducts(combined.length > 0 ? combined : FALLBACK_PRODUCTS);

        if (catRes.ok && catJSON.success) {
          setCategories(Array.isArray(catJSON.data) ? catJSON.data : []);
        } else {
          setCategories(FALLBACK_CATEGORIES);
        }
      } catch (err) {
        console.error('API Error:', err);
        setError('Failed to connect to server.');
        setAllProducts(FALLBACK_PRODUCTS);
        // Use fallback categories so Browse by Category still renders
        setCategories(FALLBACK_CATEGORIES);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fetchConfig, FALLBACK_PRODUCTS, FALLBACK_CATEGORIES]);

  // Also apply fallback if API returned empty arrays
  useEffect(() => {
    if (!loading && categories.length === 0) {
      setCategories(FALLBACK_CATEGORIES);
    }
  }, [loading, categories.length, FALLBACK_CATEGORIES]);

const NO_IMAGE_PLACEHOLDER = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPSczMDAnIGhlaWdodD0nMzAwJyB2aWV3Qm94PScwIDAgMzAwIDMwMCc+PHJlY3Qgd2lkdGg9JzEwMCUnIGhlaWdodD0nMTAwJScgZmlsbD0nI2YxZjVmOScvPjx0ZXh0IHg9JzUwJScgeT0nNTAlJyBkb21pbmFudC1iYXNlbGluZT0nbWlkZGxlJyB0ZXh0LWFuY2hvcj0nbWlkZGxlJyBmb250LWZhbWlseT0nc2Fucy1zZXJpZicgZm9udC1zaXplPScxNicgZmlsbD0nIzk0YTNiOCc+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+";

  const getImage = (imgName) => {
    if (!imgName) return NO_IMAGE_PLACEHOLDER;
    if (imgName.startsWith('http://') || imgName.startsWith('https://') || imgName.startsWith('data:')) {
      if (imgName.includes('ik.imagekit.io')) {
        const hasParams = imgName.includes('?');
        return imgName + (hasParams ? '&' : '?') + 'tr=w-500,q-80,f-auto';
      }
      return imgName;
    }
    
    const cleanImgName = (imgName.startsWith('/uploads/') || imgName.startsWith('uploads/')) ? imgName : `/uploads/${imgName}`;
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.') || window.location.hostname.startsWith('172.') || window.location.hostname.startsWith('10.') || window.location.port !== '';
    let backendBase = isLocalHost ? 'http://localhost:5000' : fetchConfig.apiUrl.replace('/api', '');
    if (!backendBase || backendBase.startsWith('/')) {
      backendBase = `${window.location.protocol}//${window.location.hostname}:5000`;
    }
    return `${backendBase}${cleanImgName.startsWith('/') ? '' : '/'}${cleanImgName}`;
  };

  const filteredProducts = allProducts.filter(prod => {
    const otherProductQuery = searchParams.get('otherProductCategory');
    if (otherProductQuery) {
      const prodOtherId = prod.otherProductCategory?._id || prod.otherProductCategory;
      if (prodOtherId !== otherProductQuery) {
        return false;
      }
    }

    const name = prod && prod.name ? String(prod.name).toLowerCase() : '';
    const desc = prod && prod.description ? String(prod.description).toLowerCase() : '';
    const query = search ? String(search).toLowerCase() : '';
    return name.includes(query) || desc.includes(query);
  });
  console.log('DEBUG: Filtered products:', filteredProducts.map(p => p.name), 'for query ID:', searchParams.get('otherProductCategory'));

  const sortedCategories = useMemo(() => {
    return [...categories]
      .map(cat => {
        const count = cat.productCount !== undefined ? cat.productCount : allProducts.filter(p => (p.category?._id || p.category) === cat._id).length;
        return { ...cat, productCount: count };
      })
      .filter(cat => cat.productCount > 0)
      .sort((a, b) => b.productCount - a.productCount);
  }, [categories, allProducts]);

  return (
    <div className="products-page">
      <Header />

      {/* Hero */}
      <section className="products-hero" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="products-hero-overlay"></div>
        <div className="container">
          <div className="products-hero-content">
            <span className="products-hero-badge">{t('products_page.hero_badge')}</span>
            <h1 className="products-hero-title">{t('products_page.hero_title')}</h1>
            <p className="products-hero-sub">{t('products_page.hero_sub')}</p>
            <div ref={searchRef} className="products-search-container">
              <div className="products-search-wrap">
                <svg className="search-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4BA83D" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <input
                  type="text"
                  placeholder={t('products_page.search_placeholder')}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && suggestions.length > 0) {
                      const item = suggestions[0];
                      if (item.type === 'product') {
                        navigate(`/product/${item.slug}`);
                      } else {
                        navigate(`/category/${item.slug}`);
                      }
                      setShowSuggestions(false);
                      setSearch('');
                    }
                  }}
                  className="products-search-input"
                />
              </div>

              {showSuggestions && suggestions.length > 0 && (
                <div className="search-suggestions-dropdown">
                  {suggestions.map((item) => {
                    const isProduct = item.type === 'product';
                    return (
                    <div 
                      key={`${item.type}_${item._id}`} 
                      className="suggestion-item"
                      onClick={() => {
                        if (isProduct) {
                          navigate(`/product/${item.slug}`);
                        } else {
                          navigate(`/category/${item.slug}`);
                        }
                        setShowSuggestions(false);
                        setSearch('');
                      }}
                    >
                      {isProduct ? (
                        <img 
                          src={getImage(item.images && item.images[0])} 
                          alt={item.name} 
                          className="suggestion-thumb" 
                        />
                      ) : (
                        <div className="suggestion-thumb" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#eef8ee', color: '#4BA83D', fontWeight: 'bold', fontSize: '12px' }}>
                          CAT
                        </div>
                      )}
                      <div className="suggestion-info">
                        <span className="suggestion-name">{item.name} {isProduct ? '' : <span style={{fontSize: '11px', color: '#888', fontWeight: 'normal', marginLeft: '6px'}}>(Category)</span>}</span>
                        <span className="suggestion-desc">
                          {item.description ? item.description.substring(0, 60) + '...' : ''}
                        </span>
                      </div>
                    </div>
                  )})}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="products-stats-bar">
        <div className="container">
          <div className="products-stats-grid">
            {[
              { value: '150+', label: t('products_page.stats.products'), color: '#4ba242' },
              { value: `${categories.length}`,    label: t('products_page.stats.categories'), color: '#e8762e' },
              { value: '20+',    label: t('products_page.stats.brands'),     color: '#f5a623' },
              { value: '100%', label: t('products_page.stats.genuine'),    color: '#4ba242' },
            ].map((s, i) => (
              <div key={i} className="products-stat-item" style={{ '--stat-color': s.color }}>
                <span className="products-stat-value" style={{ color: s.color }}>{s.value}</span>
                <span className="products-stat-label">{s.label}</span>
              </div>
            ))}
            {/* WhatsApp Chat Icon — right of Genuine */}
            <div className="stats-wa-btn">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.438 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.414 0 .004 5.412 0 12.046c0 2.123.555 4.195 1.608 6.014L0 24l6.117-1.605a11.782 11.782 0 005.925 1.587h.005c6.632 0 12.043-5.414 12.047-12.048a11.821 11.821 0 00-3.526-8.513"/>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="product-categories-section">
        <div className="container">
          <div className="categories-header-centered">
            <h2 className="categories-title-centered">{t('products_page.browse_cat')}</h2>
            <p className="categories-sub-centered">{t('products_page.browse_cat_sub')}</p>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--primary-color)' }}>{t('products_page.loading_cat')}</div>
          ) : (
            <div className="categories-grid-new">
              {sortedCategories.map((cat, i) => {
                const themeColors = ['green', 'orange', 'yellow'];
                const theme = themeColors[i % 3];
                const themeHexes = { green: '#4BA83D', orange: '#e8762e', yellow: '#fbc02d' };
                const iconColor = themeHexes[theme];
                const title = cat.name;
                const slug = cat.slug;
                const desc = cat.description || `Explore high-quality ${cat.name} agricultural solutions.`;
                const count = cat.productCount !== undefined ? cat.productCount : allProducts.filter(p => (p.category?._id || p.category) === cat._id).length;
                const countText = `${count} Product(s)`;

                return (
                  <div 
                    key={cat._id || i} 
                    className={`category-card-new theme-${theme}`}
                    onClick={() => navigate(`/category/${slug}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="cat-icon-wrap">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <path d="M13.3315 14.6647L27.9963 15.8646C28.3262 15.9294 28.6195 16.1166 28.8173 16.3886C29.015 16.6605 29.1028 16.9972 29.0628 17.3311L28.1763 22.8743C28.1261 23.1875 27.966 23.4726 27.7246 23.6784C27.4832 23.8841 27.1763 23.997 26.8591 23.9968H26.6631" stroke={iconColor} strokeWidth="2.66632" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M21.3303 23.9969H14.6646" stroke={iconColor} strokeWidth="2.66632" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M23.9967 6.66583C23.6432 6.66583 23.3041 6.80629 23.054 7.05631C22.804 7.30632 22.6636 7.64542 22.6636 7.99899V15.4287" stroke={iconColor} strokeWidth="2.66632" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M3.99951 5.33258H14.8368C15.1586 5.33276 15.4694 5.44934 15.712 5.6608C15.9546 5.87226 16.1125 6.16433 16.1566 6.4831L17.3311 14.9926" stroke={iconColor} strokeWidth="2.66632" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.33252 14.6647V5.33258" stroke={iconColor} strokeWidth="2.66632" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9.33203 19.9974H9.34536" stroke={iconColor} strokeWidth="2.66632" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10.665 13.4648V5.33258" stroke={iconColor} strokeWidth="2.66632" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M23.9969 26.6631C25.4694 26.6631 26.6632 25.4694 26.6632 23.9968C26.6632 22.5243 25.4694 21.3305 23.9969 21.3305C22.5243 21.3305 21.3306 22.5243 21.3306 23.9968C21.3306 25.4694 22.5243 26.6631 23.9969 26.6631Z" stroke={iconColor} strokeWidth="2.66632" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9.3323 26.6632C13.0137 26.6632 15.9981 23.6788 15.9981 19.9974C15.9981 16.316 13.0137 13.3316 9.3323 13.3316C5.65088 13.3316 2.6665 16.316 2.6665 19.9974C2.6665 23.6788 5.65088 26.6632 9.3323 26.6632Z" stroke={iconColor} strokeWidth="2.66632" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3 className="cat-title">{title}</h3>
                    <p className="cat-desc">{desc}</p>
                    <div className="cat-footer">
                      <span className="cat-count-pill">{countText}</span>
                      <svg className="cat-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </div>
                    <div className="cat-explore">{t('products_page.explore_cat')}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* All Products Section */}
      <section className="all-products-section">
        <div className="container">
          <div className="all-products-header">
            <h2 className="all-products-title">{t('products_page.all_products')}</h2>
            <p className="all-products-sub">{filteredProducts.length} {t('products_page.products_avail')}</p>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--primary-color)' }}>{t('products_page.loading_prod')}</div>
          ) : error && filteredProducts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--accent-color)' }}>{error}</div>
          ) : filteredProducts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'gray' }}>{t('products_page.no_prod_search')}</div>
          ) : (
            <div className="all-products-grid">
              {filteredProducts.map((prod, i) => (
                <div key={prod._id || i} className="all-product-card">
                  <div className="prod-img-wrap" style={{ backgroundImage: `url(${getImage(prod.images && prod.images[0], prod.name)})` }}>
                    <span className="prod-badge-popular">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                      {t('products_page.popular')}
                    </span>
                  </div>
                  <div className="prod-info">
                    <h3 className="prod-title">{prod.name}</h3>
                    <p className="prod-desc">{prod.description}</p>
                    <div className="prod-actions">
                      {prod.slug ? (
                        <button className="btn-details" onClick={() => navigate(`/product/${prod.slug}`)}>{t('products_page.details')}</button>
                      ) : (
                        <div style={{ flex: 1 }}></div>
                      )}
                      <button className="btn-wa" onClick={() => navigate(`/enquire?product=${prod.slug || prod._id}&productName=${encodeURIComponent(prod.name)}`)}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Need Help CTA Section */}
      <section className="need-help-section">
        <div className="container">
          <div className="need-help-box">
            <div className="help-icon-wrap">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#e8762e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="7"></circle>
                <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
              </svg>
            </div>
            <h2 className="help-title">{t('products_page.need_help')}</h2>
            <p className="help-sub">{t('products_page.help_sub')}</p>
            <div className="help-actions">
              <button className="btn-call-expert" onClick={() => navigate('/call-expert')}>{t('products_page.call_expert')}</button>
              <button className="btn-whatsapp-us" onClick={() => window.open('https://wa.me/919631277264', '_blank')}>{t('products_page.whatsapp_us')}</button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;


